import * as React from 'react'
import { useMutationPaymentCheckTest } from '@/modules/shared/query-client/useMutationPaymentCheckTest'
import {
  useQueryPaymentCheckCompanies,
  PanelStatus,
} from '@/modules/shared/query-client/useQueryPaymentCheckCompanies'

const nipSourceBadge: Record<
  'config' | 'operational' | 'none',
  { label: string; cls: string; title: string }
> = {
  config: {
    label: 'Konfiguracja',
    cls: 'tw-bg-blue-100 tw-text-blue-800',
    title: 'NIP pobrany z konfiguracji firmy (CompanyConfiguration)',
  },
  operational: {
    label: 'Ustawienia firmy',
    cls: 'tw-bg-amber-100 tw-text-amber-800',
    title: 'Brak NIP w konfiguracji — pobrany z ustawień firmy (baza operacyjna)',
  },
  none: {
    label: 'Brak',
    cls: 'tw-bg-gray-200 tw-text-gray-600',
    title: 'Brak NIP zarówno w konfiguracji, jak i w ustawieniach firmy',
  },
}

const panelStatusBadge: Record<PanelStatus, { label: string; cls: string }> = {
  paid: { label: 'Opłacone', cls: 'tw-bg-green-100 tw-text-green-800' },
  unpaid: { label: 'Nieopłacone', cls: 'tw-bg-red-100 tw-text-red-800' },
  not_in_panel: { label: 'Brak w panelu', cls: 'tw-bg-gray-100 tw-text-gray-600' },
  no_nip: { label: 'Brak NIP', cls: 'tw-bg-amber-100 tw-text-amber-800' },
  error: { label: 'Błąd', cls: 'tw-bg-red-100 tw-text-red-700' },
}

const formatDate = (iso: string | null) => (iso ? iso.slice(0, 10) : '—')

export const PaymentCheckTab: React.FC = () => {
  const mutation = useMutationPaymentCheckTest()
  const result = mutation.data

  const companies = useQueryPaymentCheckCompanies()

  const tone = !result
    ? 'neutral'
    : result.reachable && result.proxyEffective
    ? 'ok'
    : result.reachable
    ? 'warn'
    : 'bad'

  const toneClasses: Record<string, string> = {
    ok: 'tw-bg-green-100 tw-text-green-800',
    warn: 'tw-bg-amber-100 tw-text-amber-800',
    bad: 'tw-bg-red-100 tw-text-red-800',
    neutral: 'tw-bg-gray-100 tw-text-gray-700',
  }

  const toneLabel: Record<string, string> = {
    ok: '✓ POŁĄCZENIE OK',
    warn: '⚠ OSIĄGNIĘTO, ALE PROXY NIEAKTYWNE',
    bad: '✗ BRAK POŁĄCZENIA',
    neutral: '—',
  }

  return (
    <div className="tw-p-6 tw-max-w-5xl">
      <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800 tw-mb-2">
        Test połączenia z panelem płatności
      </h3>
      <p className="tw-text-sm tw-text-gray-600 tw-mb-6">
        Wysyła testowe zapytanie do panelu płatności przez skonfigurowane proxy (tą samą
        ścieżką co weryfikacja płatności). Panel jest osiągalny tylko przez proxy — bezpośredni
        ruch zwraca 403.
      </p>

      <button
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="tw-rounded tw-px-5 tw-py-2 tw-text-white tw-text-sm tw-font-semibold tw-cursor-pointer tw-bg-blue-600 hover:tw-bg-blue-700 disabled:tw-opacity-50 tw-mb-6"
      >
        {mutation.isPending ? 'Testowanie...' : 'Testuj połączenie'}
      </button>

      {mutation.isError && (
        <div className="tw-rounded tw-border tw-border-red-200 tw-bg-red-50 tw-p-4 tw-text-sm tw-text-red-700 tw-mb-4">
          Nie udało się wykonać testu (błąd żądania do API).
        </div>
      )}

      {result && (
        <div className="tw-rounded tw-border tw-border-gray-200 tw-p-4 tw-bg-gray-50 tw-mb-8">
          <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
            <span
              className={`tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold ${toneClasses[tone]}`}
            >
              {toneLabel[tone]}
            </span>
            {result.statusCode != null && (
              <span className="tw-text-xs tw-text-gray-500">
                HTTP {result.statusCode}
              </span>
            )}
            <span className="tw-text-xs tw-text-gray-500">{result.elapsedMs} ms</span>
          </div>

          <p className="tw-text-sm tw-text-gray-700 tw-mb-4">{result.reason}</p>

          <dl className="tw-grid tw-grid-cols-[auto_1fr] tw-gap-x-4 tw-gap-y-1 tw-text-xs">
            <dt className="tw-text-gray-500">Panel (CheckUrl)</dt>
            <dd className="tw-text-gray-800 tw-break-all">{result.checkUrl}</dd>

            <dt className="tw-text-gray-500">Proxy</dt>
            <dd className="tw-text-gray-800">
              {result.proxyConfigured ? result.proxyHost : 'nie skonfigurowane'}
            </dd>

            <dt className="tw-text-gray-500">Token</dt>
            <dd className="tw-text-gray-800">
              {result.bearerTokenConfigured ? 'skonfigurowany' : 'BRAK'}
            </dd>

            {result.responseSnippet && (
              <>
                <dt className="tw-text-gray-500">Odpowiedź</dt>
                <dd className="tw-text-gray-800 tw-break-all tw-font-mono">
                  {result.responseSnippet}
                </dd>
              </>
            )}
          </dl>
        </div>
      )}

      <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
        <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800">
          Firmy rozliczane przez panel płatności
        </h3>
        <button
          onClick={() => companies.refreshForced()}
          disabled={companies.isFetching}
          className="tw-rounded tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-cursor-pointer tw-text-blue-700 tw-border tw-border-blue-300 hover:tw-bg-blue-50 disabled:tw-opacity-50"
        >
          {companies.isFetching ? 'Odświeżanie...' : 'Odśwież (ponów sprawdzenie)'}
        </button>
      </div>
      <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
        Firmy z rozliczeniem zewnętrznym (manualnym) wraz z aktualnym statusem płatności
        pobranym z panelu. Statusy są zapamiętywane w pamięci serwera przez 15 minut.
      </p>

      {companies.isLoading && (
        <div className="tw-text-sm tw-text-gray-500">Ładowanie...</div>
      )}
      {companies.isError && (
        <div className="tw-rounded tw-border tw-border-red-200 tw-bg-red-50 tw-p-4 tw-text-sm tw-text-red-700">
          Nie udało się pobrać listy firm.
        </div>
      )}

      {companies.data && (
        <div className="tw-overflow-x-auto tw-rounded tw-border tw-border-gray-200">
          <table className="tw-w-full tw-text-sm">
            <thead className="tw-bg-gray-50 tw-text-gray-600 tw-uppercase tw-text-xs">
              <tr>
                <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold">Firma</th>
                <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold">NIP</th>
                <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold">Status panelu</th>
                <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold">Nasz status</th>
                <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold">Abonament do</th>
              </tr>
            </thead>
            <tbody>
              {companies.data.length === 0 && (
                <tr>
                  <td colSpan={5} className="tw-px-3 tw-py-4 tw-text-gray-500 tw-text-center">
                    Brak firm rozliczanych przez panel.
                  </td>
                </tr>
              )}
              {companies.data.map((c) => {
                const badge = panelStatusBadge[c.panelStatus]
                return (
                  <tr key={c.companyId} className="tw-border-t tw-border-gray-100 hover:tw-bg-gray-50">
                    <td className="tw-px-3 tw-py-2 tw-align-top tw-text-gray-800">
                      <div>{c.companyName}</div>
                      {c.billedViaServiceAccount && (
                        <span
                          className="tw-inline-block tw-mt-1 tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-bg-indigo-100 tw-text-indigo-800"
                          title={`Rozliczana parasolowo przez konto usługowe „${
                            c.serviceAccountName ?? ''
                          }" — płatność sprawdzana po NIP tego konta.`}
                        >
                          ↳ konto usługowe{c.serviceAccountName ? `: ${c.serviceAccountName}` : ''}
                        </span>
                      )}
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-align-top tw-text-gray-700">
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <span>{c.nip ?? '—'}</span>
                        <span
                          className={`tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold ${nipSourceBadge[c.nipSource].cls}`}
                          title={nipSourceBadge[c.nipSource].title}
                        >
                          {nipSourceBadge[c.nipSource].label}
                        </span>
                      </div>
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-align-top">
                      <span
                        className={`tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold ${badge.cls}${
                          c.panelError ? ' tw-cursor-help' : ''
                        }`}
                        title={c.panelError ?? undefined}
                      >
                        {badge.label}
                      </span>
                      {c.panelStatusCode != null && (
                        <span
                          className="tw-ml-1 tw-text-xs tw-text-gray-400"
                          title={c.panelError ?? undefined}
                        >
                          {c.panelStatusCode}
                        </span>
                      )}
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-align-top tw-text-gray-700">
                      {c.permanentlyBlocked ? (
                        <span className="tw-text-red-700 tw-font-semibold">Zablokowana</span>
                      ) : c.paid ? (
                        <span className="tw-text-green-700">Opłacona</span>
                      ) : (
                        <span className="tw-text-gray-500">Nieopłacona</span>
                      )}
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-align-top tw-text-gray-700">
                      {formatDate(c.subscriptionExpirationDate)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
